import { useEffect, useState } from "react";
import { CalendarClock, Plus } from "lucide-react";
import type { Need } from "../../lib/coverage";
import type { DashboardData, Owner, Recurring, Transaction } from "../../types";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import PayBill from "./PayBill";
import BillCard from "./BillCard";
import BillDetail from "./BillDetail";
import BillForm from "./BillForm";
import { blankBill, billToDraft, type BillDraft } from "./bill-draft";
import { api } from "../../lib/api";
import { money } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import SecondaryHeading from "../../app/SecondaryHeading";
import Modal from "../../components/Modal";
import { removePendingRecord, saveBillOrQueue } from "../../lib/pending-records";
import { syncPendingEntries } from "../../lib/sync-pending-entries";

export default function Bills({
  data,
  ensure,
  profile,
  onSaved,
  onOpenTransaction,
  focusId,
  onFocusHandled,
}: {
  data: DashboardData;
  /** Loads the ledger back far enough for what is open. */
  ensure: (need: Need) => unknown;
  profile: Owner;
  onSaved: () => Promise<unknown>;
  onOpenTransaction: (t: Transaction) => void;
  focusId?: string | null;
  onFocusHandled?: () => void;
}) {
  const [draft, setDraft] = useState<BillDraft | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [paying, setPaying] = useState<Recurring | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [filter, setFilter] = useState<"all" | "soon" | "income" | "paused">("all");

  useEffect(() => {
    if (!focusId) return;
    setDetailId(focusId);
    onFocusHandled?.();
  }, [focusId, onFocusHandled]);

  const bills = [...(data.recurring || [])].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return String(a.nextDue || "").localeCompare(String(b.nextDue || ""));
  });
  const detail = bills.find((r) => r.id === detailId) || null;
  const pendingDetail = !!detailId?.startsWith("local:bill:");
  const today = new Date().toISOString().slice(0, 10);

  const activeBills = bills.filter((b) => b.active);
  const pausedBills = bills.filter((b) => !b.active);
  const incomeBills = bills.filter((b) => b.type === "Income");

  const dueSoonBills = activeBills.filter((b) => {
    if (!b.nextDue) return false;
    const days = Math.round(
      (Date.parse(`${b.nextDue.slice(0, 10)}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86400000,
    );
    return days <= 7;
  });

  const monthlyOutflow = activeBills
    .filter((b) => b.type !== "Income")
    .reduce((sum, b) => {
      const freq = (b.frequency || "monthly").toLowerCase();
      let m = b.amount || 0;
      if (freq === "weekly") m = (m * 52) / 12;
      else if (freq === "yearly") m = m / 12;
      return sum + m;
    }, 0);

  const monthlyInflow = activeBills
    .filter((b) => b.type === "Income")
    .reduce((sum, b) => {
      const freq = (b.frequency || "monthly").toLowerCase();
      let m = b.amount || 0;
      if (freq === "weekly") m = (m * 52) / 12;
      else if (freq === "yearly") m = m / 12;
      return sum + m;
    }, 0);

  const filteredBills = bills.filter((b) => {
    if (filter === "soon") return dueSoonBills.includes(b);
    if (filter === "income") return b.type === "Income";
    if (filter === "paused") return !b.active;
    return true;
  });

  async function save() {
    if (!draft || busy) return;
    setBusy(true);
    setError("");
    try {
      const action = draft.id ? "update" as const : "create" as const;
      const result = await saveBillOrQueue(profile, draft, action, (ready) => api(draft.id ? "bills/update" : "bills/create", {
        requestId: crypto.randomUUID(),
        ...ready,
        destinationAccountId: ready.type === "Transfer" ? ready.destinationAccountId : undefined,
      }));
      setDraft(null);
      if (result.queued) {
        if (navigator.onLine) void syncPendingEntries().then(() => onSaved()).catch(() => undefined);
      } else await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deletePendingBill() {
    if (!detailId?.startsWith("local:bill:") || busy) return;
    setBusy(true);
    setError("");
    try {
      removePendingRecord(detailId.slice("local:bill:".length));
      setDetailId(null);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function archive(bill: Recurring) {
    setBusy(true);
    setError("");
    try {
      await api("bills/archive", { id: bill.id, expectedUpdatedAt: bill.updatedAt });
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function skip(bill: Recurring) {
    setBusy(true);
    setError("");
    try {
      await api("bills/skip", { requestId: crypto.randomUUID(), scheduledItemId: bill.id });
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel account-settings recurring-view">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <SecondaryHeading title="Recurring" icon={<CalendarClock size={15} />}>
        <button type="button" className="primary small" onClick={() => { setDraft(blankBill(data)); setError(""); }}>
          <Plus size={13} strokeWidth={2.5} /> Add
        </button>
      </SecondaryHeading>

      {error && <div className="inline-error" role="alert">{error}</div>}

      {bills.length > 0 && (
        <div className="recurring-summary-bar">
          <div className="recurring-summary-stat">
            <span className="recurring-summary-label">Monthly Outflow</span>
            <strong className="recurring-summary-val">
              <Private>{money(monthlyOutflow, data.baseCurrency || "USD")}</Private>
              <small>/mo</small>
            </strong>
          </div>
          {monthlyInflow > 0 && (
            <div className="recurring-summary-stat">
              <span className="recurring-summary-label">Expected Income</span>
              <strong className="recurring-summary-val income">
                <Private>+{money(monthlyInflow, data.baseCurrency || "USD")}</Private>
                <small>/mo</small>
              </strong>
            </div>
          )}
          <div className="recurring-summary-stat">
            <span className="recurring-summary-label">Commitment</span>
            <div className="recurring-summary-meta">
              <span className="recurring-meta-pill">
                {activeBills.length} active
              </span>
              {dueSoonBills.length > 0 && (
                <span className="recurring-meta-pill highlight">
                  {dueSoonBills.length} due soon
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {bills.length > 0 && (dueSoonBills.length > 0 || incomeBills.length > 0 || pausedBills.length > 0) && (
        <div className="recurring-filter-tabs" role="tablist" aria-label="Filter recurring bills">
          <button
            type="button"
            role="tab"
            aria-selected={filter === "all"}
            className={`recurring-tab-btn${filter === "all" ? " active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All <span>({bills.length})</span>
          </button>
          {dueSoonBills.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "soon"}
              className={`recurring-tab-btn${filter === "soon" ? " active" : ""}`}
              onClick={() => setFilter("soon")}
            >
              Due soon <span>({dueSoonBills.length})</span>
            </button>
          )}
          {incomeBills.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "income"}
              className={`recurring-tab-btn${filter === "income" ? " active" : ""}`}
              onClick={() => setFilter("income")}
            >
              Income <span>({incomeBills.length})</span>
            </button>
          )}
          {pausedBills.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "paused"}
              className={`recurring-tab-btn${filter === "paused" ? " active" : ""}`}
              onClick={() => setFilter("paused")}
            >
              Paused <span>({pausedBills.length})</span>
            </button>
          )}
        </div>
      )}

      {draft && (
        <Modal title={draft.id ? "Edit recurring" : "New recurring"} onClose={() => { setDraft(null); setError(""); }}>
          <BillForm
            draft={draft}
            setDraft={setDraft}
            data={data}
            ensure={ensure}
            busy={busy}
            onSubmit={() => void save()}
            onCancel={() => { setDraft(null); setError(""); }}
          />
        </Modal>
      )}

      <div className="recurring-list">
        {filteredBills.map((bill) => (
          <BillCard
            key={bill.id}
            bill={bill}
            today={today}
            busy={busy}
            onOpen={() => setDetailId(bill.id)}
            onPay={() => setPaying(bill)}
            onSkip={() => skip(bill)}
            onEdit={() => { setDraft(billToDraft(bill)); setError(""); }}
            onArchive={() => setConfirm({
              title: "Archive this bill?",
              message: `It stops showing as ${bill.type === "Income" ? "expected" : "due"}. Its history stays available.`,
              confirmLabel: "Archive",
              onConfirm: () => archive(bill),
            })}
          />
        ))}
        {bills.length > 0 && !filteredBills.length && (
          <div className="empty-small iou-empty">
            <p>No bills match this filter.</p>
          </div>
        )}
        {!bills.length && (
          <div className="empty-small iou-empty">
            <CalendarClock size={28} />
            <p>Nothing recurring yet. Add a rent, subscription or salary to track when it's due.</p>
          </div>
        )}
      </div>
      {detail && pendingDetail && (
        <Modal title="Not synced" onClose={() => setDetailId(null)}>
          <p>Saved on this device. It will sync automatically when you’re online.</p>
          <p><strong>{detail.name}</strong></p>
          {error && <div className="inline-error" role="alert">{error}</div>}
          <button
            type="button"
            className="text-button subtle danger"
            disabled={busy}
            onClick={() =>
              setConfirm({
                title: "Delete unsynced bill?",
                message: "This bill was saved on this device and hasn’t been synced yet. Deleting it will remove it completely.",
                confirmLabel: "Delete bill",
                danger: true,
                onConfirm: () => deletePendingBill(),
              })
            }
          >
            Delete unsynced bill
          </button>
        </Modal>
      )}
      {detail && !pendingDetail && (
        <BillDetail
          bill={detail}
          data={data}
          ensure={ensure}
          today={today}
          onOpenTransaction={onOpenTransaction}
          onClose={() => setDetailId(null)}
          onEdit={() => {
            setDetailId(null);
            setDraft(billToDraft(detail));
            setError("");
          }}
          onArchive={() =>
            setConfirm({
              title: "Archive this bill?",
              message: `It stops showing as ${detail.type === "Income" ? "expected" : "due"}. Its history stays available.`,
              confirmLabel: "Archive",
              onConfirm: () => archive(detail),
            })
          }
        />
      )}
      {paying && (
        <PayBill
          data={data}
          profile={profile}
          bill={paying}
          onClose={() => setPaying(null)}
          onPaid={onSaved}
        />
      )}
    </section>
  );
}
